/**
 * SaveDesignDialog — modal for saving / renaming a design.
 *
 * Two modes:
 *   - `mode="create"`: POST /api/designs with the current payload. On
 *     success, navigates to /app/{module}/{newId} so the URL reflects
 *     the saved design.
 *   - `mode="update"`: PATCH /api/designs/{id} with the latest payload
 *     plus any edits to name / description / project.
 *
 * Each design page (columns, vigas, etc.) calls this with its current
 * request body — no global store coupling. Parent decides when to open
 * and supplies the payload via props.
 */

import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Dialog from '@radix-ui/react-dialog';
import { Save, X, Loader2, Infinity as InfinityIcon } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { FierroIcon } from '@/components/icons/FierroIcon';
import {
  type DesignFull,
  type DesignModule,
} from '@/features/designs/types';
import { useCreateDesign, useUpdateDesign } from '@/features/designs/useDesigns';
import { useAuthStore } from '@/stores/authStore';

/** Cost in fierros to create a new design, mirrors backend SAVE_COSTS. */
const SAVE_COSTS: Record<DesignModule, number> = {
  predim: 1,
  columns: 2,
  beams: 2,
  losas: 2,
  muros: 2,
  zapatas: 2,
  // Análisis estructural: guardar es gratis (es cálculo previo al diseño,
  // no entregable). Coincide con SAVE_COSTS del backend.
  analysis_beams: 0,
  analysis_columns: 0,
  analysis_losas: 0,
  analysis_muros: 0,
  analysis_zapatas: 0,
};

function isUnlimited(): boolean {
  const u = useAuthStore.getState().user;
  if (!u) return false;
  if (u.role?.slug === 'subscriber') return true;
  if ((u.role?.permissions ?? []).includes('billing.unlimited')) return true;
  if (
    u.subscription_status === 'active' &&
    u.subscription_expires_at &&
    new Date(u.subscription_expires_at).getTime() > Date.now()
  )
    return true;
  return false;
}

interface SaveDesignDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The full design page state to persist. */
  module: DesignModule;
  subtype?: string | null;
  payload: Record<string, unknown>;
  /** Pass the existing design when editing (PATCH mode); omit for create. */
  existing?: DesignFull | null;
  /** Notified with the saved record (so parent can update its local URL). */
  onSaved?: (saved: DesignFull) => void;
}

export function SaveDesignDialog({
  open,
  onOpenChange,
  module,
  subtype,
  payload,
  existing,
  onSaved,
}: SaveDesignDialogProps) {
  const create = useCreateDesign();
  const update = useUpdateDesign();
  const navigate = useNavigate();

  const isUpdate = !!existing;
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [project, setProject] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Initialise the form whenever the dialog opens (or the existing record changes).
  useEffect(() => {
    if (!open) return;
    setName(existing?.name ?? '');
    setDescription(existing?.description ?? '');
    setProject(existing?.project ?? '');
    setError(null);
  }, [open, existing?.id]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError('Ponle un nombre al diseño para encontrarlo después.');
      return;
    }
    try {
      let saved: DesignFull;
      if (isUpdate && existing) {
        saved = await update.mutateAsync({
          id: existing.id,
          data: {
            name: name.trim(),
            description: description.trim(),
            project: project.trim(),
            subtype: subtype ?? existing.subtype,
            payload,
          },
        });
      } else {
        saved = await create.mutateAsync({
          module,
          subtype: subtype ?? null,
          name: name.trim(),
          description: description.trim(),
          project: project.trim(),
          payload,
        });
      }
      onOpenChange(false);
      onSaved?.(saved);
      // Newly created: update the URL so the user can reload / share / etc.
      if (!isUpdate) {
        navigate(`/app/${routeFor(module)}/${saved.id}`, { replace: true });
      }
    } catch (err) {
      // Special-case 402 (insufficient tokens): the axios interceptor in
      // lib/api.ts already pops the global InsufficientTokensModal with
      // its own CTA flow ("Comprar fierros" / "Suscribirse"). Closing
      // this dialog avoids stacking two modals on top of each other.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const status = (err as any)?.response?.status;
      if (status === 402) {
        onOpenChange(false);
        return;
      }
      const detail = err && typeof err === 'object' && 'response' in err
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ? (err as any).response?.data?.detail
        : null;
      const msg =
        typeof detail === 'string'
          ? detail
          : typeof detail === 'object' && detail && 'message' in detail
            ? String((detail as { message: unknown }).message)
            : 'No se pudo guardar.';
      setError(msg);
    }
  }

  const busy = create.isPending || update.isPending;

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className="fixed inset-0 z-50"
          style={{ background: 'rgba(2,6,23,0.65)', backdropFilter: 'blur(6px)' }}
        />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 w-[92vw] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-[var(--radius-lg)] border p-6 shadow-2xl"
          style={{ background: 'var(--surface-1)', borderColor: 'var(--border-default)' }}
        >
          <Dialog.Close
            className="absolute right-3 top-3 rounded-[var(--radius-sm)] p-1.5 transition-colors hover:bg-[var(--surface-2)]"
            aria-label="Cerrar"
          >
            <X size={16} strokeWidth={1.8} />
          </Dialog.Close>

          <div className="mb-4 flex items-center gap-3">
            <span
              className="flex h-10 w-10 items-center justify-center rounded-[var(--radius)]"
              style={{ background: 'var(--accent)', color: 'var(--surface-base)' }}
            >
              <Save size={18} strokeWidth={1.8} />
            </span>
            <div>
              <Dialog.Title className="text-base" style={{ fontFamily: 'var(--font-display)', fontWeight: 700 }}>
                {isUpdate ? 'Actualizar diseño' : 'Guardar diseño'}
              </Dialog.Title>
              <Dialog.Description className="text-[12.5px]" style={{ color: 'var(--text-secondary)' }}>
                Lo verás en tu lista la próxima vez que entres.
              </Dialog.Description>
            </div>
          </div>

          {/* Cost banner — only when creating (updates are free). */}
          {!isUpdate && (() => {
            const cost = SAVE_COSTS[module] ?? 2;
            const unlimited = isUnlimited();
            return (
              <div
                className="mb-3 flex items-center justify-between gap-2 rounded-[var(--radius-sm)] border p-2.5 text-[12px]"
                style={{
                  background: unlimited ? 'rgba(245,158,11,0.08)' : 'var(--surface-2)',
                  borderColor: unlimited ? 'rgba(245,158,11,0.40)' : 'var(--border-default)',
                  color: 'var(--text-secondary)',
                }}
              >
                <span>Costo de guardar</span>
                {unlimited ? (
                  <span className="inline-flex items-center gap-1 font-medium" style={{ color: 'var(--accent)' }}>
                    <InfinityIcon size={12} strokeWidth={2} /> incluido en tu plan
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 font-mono tabular" style={{ color: 'var(--text-primary)' }}>
                    <FierroIcon size={12} /> {cost}
                  </span>
                )}
              </div>
            );
          })()}

          <form onSubmit={onSubmit} className="space-y-3">
            <label className="block text-[12px]" style={{ color: 'var(--text-secondary)' }}>
              Nombre <span style={{ color: 'var(--accent)' }}>*</span>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.currentTarget.value)}
                placeholder="Ej. C-12 (eje 3-B)"
                autoFocus
                required
                maxLength={120}
                className="mt-1 h-10 w-full rounded-[var(--radius-sm)] border bg-[var(--surface-2)] px-3 text-sm focus:border-[var(--accent)] focus:outline-none"
                style={{ borderColor: 'var(--border-default)', color: 'var(--text-primary)' }}
              />
            </label>

            <label className="block text-[12px]" style={{ color: 'var(--text-secondary)' }}>
              Proyecto (opcional)
              <input
                type="text"
                value={project}
                onChange={(e) => setProject(e.currentTarget.value)}
                placeholder="Ej. Edificio 5 pisos · La Molina"
                maxLength={120}
                className="mt-1 h-10 w-full rounded-[var(--radius-sm)] border bg-[var(--surface-2)] px-3 text-sm focus:border-[var(--accent)] focus:outline-none"
                style={{ borderColor: 'var(--border-default)', color: 'var(--text-primary)' }}
              />
            </label>

            <label className="block text-[12px]" style={{ color: 'var(--text-secondary)' }}>
              Notas (opcional)
              <textarea
                value={description}
                onChange={(e) => setDescription(e.currentTarget.value)}
                rows={3}
                maxLength={4000}
                placeholder="Comentarios, supuestos, fuente de las cargas…"
                className="mt-1 w-full rounded-[var(--radius-sm)] border bg-[var(--surface-2)] px-3 py-2 text-sm focus:border-[var(--accent)] focus:outline-none"
                style={{ borderColor: 'var(--border-default)', color: 'var(--text-primary)' }}
              />
            </label>

            {error && (
              <div
                className="rounded-[var(--radius-sm)] border p-2 text-[12px]"
                style={{
                  background: 'rgba(220,38,38,0.08)',
                  borderColor: 'rgba(220,38,38,0.4)',
                  color: '#fca5a5',
                }}
              >
                {error}
              </div>
            )}

            <div className="flex items-center justify-end gap-2 pt-2">
              <Button type="button" variant="ghost" size="md" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button type="submit" variant="primary" size="md" disabled={busy}>
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} strokeWidth={1.8} />}
                {isUpdate ? 'Actualizar' : 'Guardar'}
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function routeFor(module: DesignModule): string {
  switch (module) {
    case 'zapatas':
      return 'footings';
    case 'losas':
      return 'slabs';
    case 'muros':
      return 'walls';
    case 'analysis_beams':
      return 'analysis/beams';
    case 'analysis_columns':
      return 'analysis/columns';
    case 'analysis_losas':
      return 'analysis/slabs';
    case 'analysis_muros':
      return 'analysis/walls';
    case 'analysis_zapatas':
      return 'analysis/footings';
    default:
      return module;
  }
}
